import {
	Suspense, lazy, useEffect, useLayoutEffect, useState
} from 'react';
import {
	Routes,
	Route,
	useLocation,
	useNavigate,
	useParams,
	useSearchParams,
	Navigate,
} from 'react-router-dom';
import SuspenseLoader from '@pages/SuspenseLoader';
import { useSetRecoilState, useRecoilState, useRecoilValue } from 'recoil';
import { jwtDecode } from 'jwt-decode';
import {
	UserID,
	EOSID,
	CompanyID,
	IsAdmin,
	JWT,
	UserRoles,
	WaitForGuest,
	JWTUserType,
	DecodedJWT,
	ShipperSettings,
	authenticatedUser,
	DomainContext,
} from '@state';
import { TJWT } from '@types';
import Redirect from '@pages/Redirect';
import { clearPendoIdentify, clearCookies, syncCookiesToLocalStorageWithValidation } from '@utils';
import { useFeatureFlag, useImpersonation } from '@hooks';
import { useIsGuest, useGetShipperSettings } from '@api';

// --> Generic Pages
import MainLayout from '@pages/MainLayout';
import FreightSafeLogin from '@pages/FreightSafe/FSLogin/FSLogin';
import FSDetails from '@pages/FreightSafe/FSDetails/FSDetails';
import { HistoricalRateCard } from '@components';
const NotFound = lazy(() => import('@pages/NotFound'));

// --> Public Pages
const Home = lazy(() => import('@pages/Public/Home'));
const Login = lazy(() => import('@pages/Public/Login'));
const ResetPassword = lazy(() => import('@pages/Public/ResetPassword'));

// --> Shipper Pages
const ShipperRouter = lazy(() => import('@pages/Shipper'));
const ShipperDashboard = lazy(() => import('@pages/Shipper/Dashboard'));
const ShipperBookingIndex = lazy(() => import('@pages/Shipper/Booking'));
const BookingDashboard = lazy(() => import('@pages/Shipper/Booking/BookingDashboard'));
const BookingCreateIndex = lazy(() => import('@pages/Shipper/Booking/BookShipment/CreateIndex'));
const BookingCreateStep1 = lazy(() => import('@pages/Shipper/Booking/BookShipment/Step1Wrapper'));
const BookingCreateMultipleIndex = lazy(() => import('@pages/Shipper/Booking/BookMultiCargoShipment/CreateIndex'));
const BookingCreateMultipleStep1 = lazy(() => import('@pages/Shipper/Booking/BookMultiCargoShipment/Step1Wrapper'));
const BookingCreateMultipleStep2 = lazy(() => import('@pages/Shipper/Booking/BookMultiCargoShipment/Step2Wrapper'));
const LinehaulBookingCreateStep1 = lazy(() => import('@pages/Shipper/Booking/BookShipment/Step1LinehaulWrapper'));
const BookingCreateStep2 = lazy(() => import('@pages/Shipper/Booking/BookShipment/Step2'));
const LinehaulBookingCreateStep2 = lazy(() => import('@pages/Shipper/Booking/BookShipment/Step2'));
const BookingLabels = lazy(() => import('@pages/Shipper/Booking/Labels'));
const QuoteComplete = lazy(() => import('@pages/Shipper/Booking/QuoteComplete'));
const ShipperQuotes = lazy(() => import('@pages/Shipper/Quotes'));
const ShipperSettingsPage = lazy(() => import('@pages/Shipper/Settings'));
const ShipperAnalytics = lazy(() => import('@pages/Shipper/Analytics'));
const ShipperCompanyRouter = lazy(() => import('@pages/Shipper/Company/'));
const ShipperCompany = lazy(() => import('@pages/Shipper/Company/Company'));
const ShipperCompanyLocationsDetails = lazy(() => import('@pages/Shipper/Company/Locations/LocationsDetails/LocationsDetails'));
const ShipperInvoices = lazy(() => import('@pages/Shipper/Invoices/'));
const ShipperInvoiceDetails = lazy(() => import('@pages/Shipper/Invoices/Detail'));
const ShipperRateCardRouter = lazy(() => import('@pages/Shipper/RateCards'));
const ShipperRateCardHome = lazy(() => import('@pages/Shipper/RateCards/Home'));
const ShipperRateCardSettings = lazy(() => import('@pages/Shipper/RateCards/Settings'));
const ShipperRateCardCreate = lazy(() => import('@pages/Shipper/RateCards/Create'));
const ShipperRateCard = lazy(() => import('@pages/Shipper/RateCards/RateCard'));
const ShipperQuote = lazy(() => import('@pages/Shipper/Quotes/Quote'));
const ShipperShipments = lazy (() => import('@pages/Shipper/Shipments'));
const ShipperShipmentDetailsPage = lazy (() => import('@pages/Shipper/Shipments/Detail'));
const ShipperToday = lazy (() => import('@pages/Shipper/Today'));
const BulkUpload = lazy(() => import('@pages/Shipper/BulkUpload'));

// --> Carrier Pages
const CarrierRouter = lazy(() => import('@pages/Carrier'));
const CarrierOverview = lazy(() => import('@pages/Carrier/Overview/Overview'));
const CarrierDashboard = lazy(() => import('@pages/Carrier/Dashboard'));
const ExclusiveOffers = lazy(() => import('@pages/Carrier/PartnersOffers/PartnersOffers'));
const CarrierRateCardRouter = lazy(() => import('@pages/Carrier/RateCards'));
const CarrierRateCardHome = lazy(() => import('@pages/Carrier/RateCards/Home'));
const CarrierRateCardSettings = lazy(() => import('@pages/Carrier/RateCards/Settings'));
const CarrierRateCardCreate = lazy(() => import('@pages/Carrier/RateCards/Create'));
const CarrierRateCard = lazy(() => import('@pages/Carrier/RateCards/RateCard'));
const CarrierYourLoads = lazy(() => import('@pages/Carrier/YourLoads/YourLoads'));
const CarrierMarketPlace = lazy(() => import('@pages/Carrier/MarketPlace/MarketPlace'));
const CarrierMarketPlaceActivity = lazy(() => import('@pages/Carrier/MarketPlaceActivity/MarketPlaceActivity'));
const BulkUploadMilestone = lazy(() => import('@pages/Carrier/BulkUpdateMilestone/BulkUpdateMilestone'));
const CarrierAnalytics = lazy(() => import('@pages/Carrier/Analytics'));
const CarrierCompanyDetailsRouter = lazy(() => import('@pages/Carrier/CompanyDetails'));
const CarrierCompanyDetails = lazy(() => import('@pages/Carrier/CompanyDetails'));
const CarrierInvoices = lazy(() => import('@pages/Carrier/Invoices/Invoices'));
const CarrierInvoiceFunding = lazy(() => import('@pages/Carrier/InvoiceFunding'));

/* 	Below are all ADMIN Pages, grouped by Top Navigation */
// --> Inbox Pages
const InboxRouter = lazy(() => import('@pages/Inbox'));
const InboxQuotes = lazy(() => import('@pages/Inbox/Quotes'));
const InboxNewShippers = lazy(() => import('@pages/Inbox/NewShippers'));
const InboxNewCarriers = lazy(() => import('@pages/Inbox/NewCarriers'));


// --> Transport
const TransportRouter = lazy(() => import('@pages/Transport'));
const AllLoads = lazy(() => import('@pages/Transport/AllLoads'));
const ShipmentDetailsPage = lazy(() => import('@pages/Transport/AllLoads/ShipmentDetailsPage'));
const ShipmentDocs = lazy(() => import('@pages/Transport/ShipmentDocs'));
const PodToBeAssigned = lazy(() => import('@pages/Transport/PodToBeAssigned'));
const SuperDockRouter = lazy(() => import('@pages/Transport/SuperDock'));
const SuperDock = lazy(() => import('@pages/Transport/SuperDock/SuperDock'));
const SuperDockDetails = lazy(() => import('@pages/Transport/SuperDock/Details'));
const SuperDockConfigure = lazy(() => import('@pages/Transport/SuperDock/Configure'));
const SuperDockAllocate = lazy(() => import('@pages/Transport/SuperDock/Allocate'));
const CarrierRecommendation = lazy(() => import('@pages/Transport/CarrierRecommendation'));

// --> Finance
const FinanceRouter = lazy(() => import('@pages/Finance'));
const FinanceShipperBilling = lazy(() => import('@pages/Finance/ShipperBilling'));
const FinanceShipperBillingDetail = lazy(() => import('@pages/Finance/ShipperBilling/Detail'));
const FinanceInvoiceReport = lazy(() => import('@pages/Finance/InvoiceReport'));
const FinanceCarrierBilling = lazy(() => import('@pages/Finance/CarrierBilling'));
const FinanceCarrierBillingDetail = lazy(() => import('@pages/Finance/CarrierBilling/Detail'));
const FinanceApproval = lazy(() => import('@pages/Finance/FinanceApproval/Pending'));
const FinanceIssueAndClaim = lazy(() => import('@pages/Finance/IssueAndClaim/Pending'));
const FinanceInvoiceManagement = lazy(() => import('@pages/Finance/InvoiceManagement'));
const InvoiceManagementDetailPage = lazy(() => import('@pages/Finance/InvoiceManagement/Detail'));
const FinanceFastPayBudget = lazy(() => import('@pages/Finance/FastPayBudget'));
const FinanceDemurrageRouter = lazy(() => import('@pages/Finance/Demurrage'));
const FinanceDemurrageRateCards = lazy(() => import('@pages/Finance/Demurrage/RateCardAdmin'));

// --> Configuration
const ConfigurationRouter = lazy(() => import('@pages/Configuration'));
const ConfigurationCompany = lazy(() => import('@pages/Configuration/Company'));
const ShipperTags = lazy(() => import('@pages/Configuration/Tags'));
const ConfigurationLocations = lazy(() => import('@pages/Configuration/Locations/LocationPage'));
const ConfigurationAutoAllocation = lazy(() => import('@pages/Configuration/AutoAllocation'));
const ConfigurationAutoAllocationDetail = lazy(() => import('@pages/Configuration/AutoAllocation/AutoAllocationDetail'));
const ConfigurationTruckTypes = lazy(() => import('@pages/Configuration/TruckTypes'));
const ConfigurationDashboard = lazy(() => import('@pages/Configuration/Dashboard'));
const ConfigurationFuelLevy = lazy(() => import('@pages/Configuration/FuelLevy'));
const ConfigurationFuelLevies = lazy(() => import('@pages/Configuration/FuelLevies'));
const ConfigurationFuelLevyDetail = lazy(() => import('@pages/Configuration/FuelLevies/Detail'));
const ConfigurationMarketplace = lazy(() => import('@pages/Configuration/Marketplace'));
const ConfigurationLocation = lazy(() => import('@pages/Configuration/Locations/ConfigureLocation'));
const ConfigurationLocationShipperDetails = lazy(() => import('@pages/Configuration/Locations/ShipperDetails/ShipperDetails.tsx'));
const ConfigurationRegisteredCarriers = lazy(() => import('@pages/Configuration/RegisteredCarriers'));
const ConfigurationRegisteredShippers = lazy(() => import('@pages/Configuration/RegisteredShipper'));
const ConfigurationDataBulkUpload = lazy(() => import('@pages/Configuration/DataBulkUpload'));
const ConfigurationRegions = lazy(() => import('@pages/Configuration/Regions'));
const ConfigurationFeatureFlags = lazy(() => import('@pages/Configuration/FeatureFlags'));

const AdminRateCardRouter = lazy(() => import('@pages/Configuration/AdminRateCards'));
const AdminRateCardHome = lazy(() => import('@pages/Configuration/AdminRateCards/Home'));
const AdminRateCardSettings = lazy(() => import('@pages/Configuration/AdminRateCards/Settings'));
const AdminRateCardGlobal = lazy(() => import('@pages/Configuration/AdminRateCards/Global'));
const AdminRateCardCreate = lazy(() => import('@pages/Configuration/AdminRateCards/Create'));
const AdminRateCard = lazy(() => import('@pages/Configuration/AdminRateCards/RateCard'));

const LinkedRateCardCreate = lazy(() => import('@pages/Configuration/LinkedRateCards/Create'));
const LinkedShipperRateCard = lazy(() => import('@pages/Configuration/LinkedRateCards/Shipper'));
const LinkedCarrierRateCard = lazy(() => import('@pages/Configuration/LinkedRateCards/Carrier'));

/* Preserve query params (e.g. ?cid) when redirecting legacy routes to their new path */
const RedirectWithSearch = ({ to }: { to: string }) => {
	const { search } = useLocation();
	return <Navigate to={`${to}${search}`} replace />;
};

/* Preserve the matched sub-path (splat) and query params when redirecting a legacy route subtree */
const RedirectSplat = ({ to }: { to: string }) => {
	const { '*': rest } = useParams();
	const { search } = useLocation();
	return <Navigate to={`${to}${rest ? `/${rest}` : ''}${search}`} replace />;
};

/* Legacy Unallocated deep-links used ?filter[search]=REF — All Loads reads ?search=REF, so rewrite the param */
const RedirectUnallocatedToAllLoads = () => {
	const [searchParams] = useSearchParams();
	const ref = searchParams.get('filter[search]') ?? searchParams.get('search');
	return <Navigate to={`/transport/all-loads${ref ? `?search=${encodeURIComponent(ref)}` : ''}`} replace />;
};

const Router = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const setUserRoles = useSetRecoilState(UserRoles);
	const setUserID = useSetRecoilState(UserID);
	const setEOSID = useSetRecoilState(EOSID);
	const setUserType = useSetRecoilState(JWTUserType);
	const setCompanyID = useSetRecoilState(CompanyID);
	const setIsAdmin = useSetRecoilState(IsAdmin);
	const setDecodedJWT = useSetRecoilState(DecodedJWT);
	const [Jwt, setJWT] = useRecoilState(JWT);
	const [waitForGuest, setWaitForGuest] = useRecoilState(WaitForGuest);
	const [initComplete, setInitComplete] = useState(false);
	const authUser = useRecoilValue(authenticatedUser);
	const setShipperSettings = useSetRecoilState(ShipperSettings);
	const setDomainContext = useSetRecoilState(DomainContext);
	const { impersonate } = useImpersonation();
	const sid = searchParams.get('sid');
	const cid = searchParams.get('cid');
	const MARGIN_PERCENTAGE_LIVE = useFeatureFlag('margin_percentage');
	const DEMURRAGE_CLAIMS_LIVE = useFeatureFlag('demurrage_claims');

	const {
		guestData,
		callGetGuestAPI
	} = useIsGuest();
	const { callGetShipperSettings } = useGetShipperSettings();

	const logoutCleanup = async () => {
		setSearchParams({});
		if ((window as any).pendo) {
			clearPendoIdentify();
		}
		await sessionStorage.clear();
		await localStorage.removeItem('jwt');
		await localStorage.removeItem('refreshToken');
		await clearCookies();
		setJWT(null);
		if (['/', '/register', '/reset'].indexOf(location.pathname) < 0) {
			let searching;
			if (location.pathname === '/login') {
				searching = location.search;
			} else {
				searching = `?backto=${encodeURIComponent(`${location.pathname}${location.search}`)}`;
			}
			navigate(`/login${searching}`);
		}
	}

	const fetchSettings = (userType: string) => {
		if (userType && userType === 'shipper') {
			callGetShipperSettings({ params: { shipper_id: `${authUser?.shipper?.id}` }, headers: { 'X-Shipper-Token': `${authUser?.shipper?.id}` } }).then((res) => {
				if (res) {
					setShipperSettings(res.data);
				}
			}).catch((err) => {
				if (err && err.cause) console.error(err);
			})
		}
	}

	const init = async () => {
		const forceLogout = searchParams.get('exit');
		if (forceLogout !== null) {
			logoutCleanup();
			return;
		}

		// Check for JWT cookies from backend and sync to localStorage for seamless login bypass
		const cookiesFound = syncCookiesToLocalStorageWithValidation();
		if (cookiesFound) {
			console.log('JWT cookies found and synced to localStorage');
		}

		const storedJWT = await localStorage.getItem('jwt');
		if (storedJWT) {
			setJWT(storedJWT);
			const decoded: TJWT = jwtDecode(storedJWT);
			setUserID(decoded.user_id);
			setEOSID(decoded.eos_id);
			setUserType(decoded.user_type);
			setCompanyID(decoded.company_id);
			setIsAdmin(decoded.is_admin);
			setUserRoles(decoded.roles);
			setDecodedJWT(decoded);
			// If user has jwt token and try to access '/', '/login', we should redirect them to inbox page
			if (['/', '/login'].indexOf(location.pathname) > -1) {
				if (decoded.is_admin) {
					navigate(`/transport/all-loads`);
				} else {
					/* Bare /shipper and /carrier are SPA routes with sensible index pages —
					   navigate internally instead of a full reload (which used to land on
					   the legacy EOS shells before the CloudFront default flip). */
					navigate(`/${decoded.user_type}`);
				}
			} else {
				fetchSettings(decoded.user_type);
			}
		} else if (['/', '/register', '/freight-safe/login', '/freight-safe/details'].indexOf(location.pathname) < 0) {

			let searching;
			if (location.pathname === '/login') {
				searching = location.search;
			} else {
				searching = `?backto=${encodeURIComponent(`${location.pathname}${location.search}`)}`;
			}
			navigate(`/login${searching}`);
		}
		await impersonate();
		setInitComplete(true);
	}

	useLayoutEffect(() => {
		if (location.pathname.startsWith('/shipper')) setDomainContext('shipper');
		else if (location.pathname.startsWith('/carrier')) setDomainContext('carrier');
		else setDomainContext(null);
	}, [location.pathname]);

	useEffect(() => {
		if (!waitForGuest) {
			init();
		}
	}, [Jwt, waitForGuest]);

	useEffect(() => {
		if (guestData) {
			const loc = ['/freight-safe/login', '/freight-safe/details'].indexOf(location.pathname) >= 0;
			if (loc) {
				setWaitForGuest(false);
				return;
			}
			if (guestData.isGuest) {
				logoutCleanup();
			} else {
				setWaitForGuest(false);
			}
		}
	}, [guestData]);

	useEffect(() => {
		callGetGuestAPI();
	}, [Jwt]);

	return (
		<Suspense fallback={<SuspenseLoader />}>
			<Routes>
				{/* PUBLIC ROUTES */}
				<Route index element={<Home />} />
				<Route path={'login'} element={<Login />} />
				<Route path={'reset'} element={<ResetPassword />} />
				<Route path={'freight-safe'}>
					<Route path={'login'} element={<FreightSafeLogin />} />
					<Route index path={'details'} element={<FSDetails />} />
				</Route>

				{/* SHIPPER/CARRIER ROUTES */}
				<Route path={'/'} element={<MainLayout initComplete={initComplete} />}>
					<Route path={'shipper'} element={<ShipperRouter key={sid} /> } >
						<Route path={'today'} element={<ShipperToday />} />
						<Route path={'invoices'} element={<ShipperInvoices />} />
						<Route path={'invoices/detail/:id'} element={<ShipperInvoiceDetails />} />
						<Route index path={'dashboard'} element={<ShipperDashboard />} />
						<Route path={'booking'} element={<ShipperBookingIndex />}>
							<Route path={'dashboard'} element={<BookingDashboard />} />
							<Route path={'create'}>
								<Route index element={<BookingCreateIndex />} />
								<Route index path={'step1'} element={<BookingCreateStep1 />} />
								<Route index path={'linehaul/step1'} element={<LinehaulBookingCreateStep1 />} />
								<Route path={'step2'} element={<Redirect path={'/shipper/booking/dashboard'} />} />
								<Route path={'step2/:id'} element={<BookingCreateStep2 />} />
								<Route path={'linehaul/step2/:id'} element={<LinehaulBookingCreateStep2 />} />
								<Route path={'label'} element={<Redirect path={'/shipper/booking/dashboard'} />} />
								<Route path={'label/:reference'} element={<BookingLabels />} />
								<Route path={'thankyou/:reference'} element={<BookingLabels />} />
								<Route path={'multi'}>
									<Route index element={<BookingCreateMultipleIndex />} />
									<Route index path={'step1'} element={<BookingCreateMultipleStep1 />} />
									<Route path={'step2'} element={<BookingCreateMultipleStep2 />} />
									<Route path={'label/:reference'} element={<BookingLabels />} />
								</Route>
							</Route>
							<Route path={'quotecomplete'} element={<QuoteComplete />} />
						</Route>
						<Route index path={'quotes'} element={<ShipperQuotes />} />
						<Route path={'orders'}>
							<Route index element={<ShipperShipments />} />
							<Route path={':shipmentID'} element={<ShipperShipmentDetailsPage />} />
						</Route>
						<Route path={'analytics'} element={<ShipperAnalytics />} />
						<Route path={'company'} element={<ShipperCompanyRouter />}>
							<Route index element={<RedirectWithSearch to={'/shipper/company/profile'} />} />
							<Route path={'profile'} element={<ShipperCompany />} />
							<Route path={'team'} element={<ShipperCompany />} />
							<Route path={'billing'} element={<ShipperCompany />} />
							<Route path={'costcentre'} element={<ShipperCompany />} />
							<Route path={'document-hub'} element={<ShipperCompany />} />
							<Route path={'locations'} element={<ShipperCompany />} />
							<Route path={'regions'} element={<ShipperCompany />} />
							<Route path={'locations/:external_id'} element={<ShipperCompanyLocationsDetails /> } />
							<Route path={'integrations'} element={<ShipperCompany />} />
						</Route>
						<Route path={'ratecards'} element={<ShipperRateCardRouter />}>
							<Route index element={<ShipperRateCardHome />} />
							<Route path={'settings'} element={<ShipperRateCardSettings />} />
							<Route path={'create'}>
								<Route index element={<ShipperRateCardCreate />} />
								<Route path={'step1'} element={<ShipperRateCardCreate />} />
								<Route path={'step2'} element={<ShipperRateCard />} />
								<Route path={'step2/:id'} element={<ShipperRateCard />} />
							</Route>
							<Route path={':id'} element={<ShipperRateCard />} />
							<Route path={':id/history'} element={<HistoricalRateCard type={'Shipper'}/>} />
							<Route path={'quote/:id'} element={<ShipperQuote />} />
						</Route>
						<Route path={'bulk-uploads'} element={<BulkUpload />} />
						<Route path={'settings'} element={<ShipperSettingsPage />} />
					</Route>
					<Route path={'carrier'} element={<CarrierRouter key={cid} />} >
						<Route index path={'overview'} element={<CarrierOverview />} />
						<Route index path={'dashboard'} element={<CarrierDashboard />} />
						<Route index path={'offers'} element={<ExclusiveOffers />} />
						<Route index path={'your-loads'} element={<CarrierYourLoads />} />
						<Route index path={'marketplace'} element={<CarrierMarketPlace />} />
						<Route index path={'marketplace-activity'} element={<CarrierMarketPlaceActivity />} />
						<Route index path={'bulk-upload'} element={<BulkUploadMilestone />} />
						<Route index path={'analytics'} element={<CarrierAnalytics />} />
						<Route path={'financials-dashboard'} element={<CarrierInvoices />} />
						<Route path={'invoice-funding'} element={<CarrierInvoiceFunding />} />
						<Route path={'fintech'} element={<RedirectWithSearch to={'/carrier/invoice-funding'} />} />
						<Route path={'financials'}>
							<Route path={'detail'} element={<FinanceCarrierBillingDetail />} />
							<Route path={'detail/:id'} element={<FinanceCarrierBillingDetail />} />
						</Route>
						<Route path={'ratecards'} element={<CarrierRateCardRouter />}>
							<Route index element={<CarrierRateCardHome />} />
							<Route path={'settings'} element={<CarrierRateCardSettings />} />
							<Route path={'create'}>
								<Route index element={<CarrierRateCardCreate />} />
								<Route path={'step1'} element={<CarrierRateCardCreate />} />
								<Route path={'step2'} element={<CarrierRateCard />} />
								<Route path={'step2/:id'} element={<CarrierRateCard />} />
							</Route>
							<Route path={':id'} element={<CarrierRateCard />} />
							<Route path={':id/history'} element={<HistoricalRateCard type={'Carrier'} />} />
						</Route>
						<Route path={'company'}>
							<Route index element={<RedirectWithSearch to='/carrier/company-details' />} />
							<Route path={'regions'} element={<RedirectWithSearch to='/carrier/company-details/regions' />} />
							<Route path={'integrations'} element={<RedirectWithSearch to='/carrier/company-details/integrations' />} />
							<Route path={'admin-carrier-settings'} element={<RedirectWithSearch to='/carrier/company-details/settings' />} />
						</Route>
						<Route path={'company-details'} element={<CarrierCompanyDetailsRouter />}>
							<Route path={'profile'} element={<CarrierCompanyDetails />} />
							<Route path={'team'} element={<CarrierCompanyDetails />} />
							<Route path={'lane'} element={<CarrierCompanyDetails />} />
							<Route path={'fleet'} element={<CarrierCompanyDetails />} />
							<Route path={'finance'} element={<CarrierCompanyDetails />} />
							<Route path={'document-hub'} element={<CarrierCompanyDetails />} />
							<Route path={'settings'} element={<CarrierCompanyDetails />} />
							<Route path={'regions'} element={<CarrierCompanyDetails />} />
							<Route path={'integrations'} element={<CarrierCompanyDetails />} />
							<Route index element={<Navigate to='profile' replace />} />
						</Route>
					</Route>
				</Route>

				{/* ADMIN ROUTES */}
				<Route path={'/'} element={<MainLayout requireAdmin initComplete={initComplete} />}>
					<Route path={'inbox'} element={<InboxRouter />}>
						<Route index element={<InboxQuotes />} />
						<Route index path={'quotes'} element={<InboxQuotes />} />
						<Route path={'new_shippers'} element={<InboxNewShippers />} />
						<Route path={'new_carriers'} element={<InboxNewCarriers />} />
					</Route>
					{/* Legacy /fulfilment shim — EOS admin Navbar/Sidebar still link here; remove after EOS-side cleanup */}
					<Route path={'fulfilment'}>
						<Route index element={<RedirectUnallocatedToAllLoads />} />
						<Route path={'unallocated'} element={<RedirectUnallocatedToAllLoads />} />
						<Route path={'superdock/*'} element={<RedirectSplat to={'/transport/superdock'} />} />
						<Route path={'carrier_recommendation'} element={<RedirectWithSearch to={'/transport/carrier_recommendation'} />} />
					</Route>
					{/* Legacy EOS admin URLs (saved favourites / bookmarks) mapped to their SPA
					    successors. CloudFront carves the registration + onboarding /admin paths
					    out to EOS before these routes ever match. */}
					<Route path={'admin'}>
						<Route index element={<Navigate to={'/transport/all-loads'} replace />} />
						<Route path={'inbox/quotes'} element={<RedirectWithSearch to={'/inbox/quotes'} />} />
						<Route path={'inbox/shippers'} element={<RedirectWithSearch to={'/inbox/new_shippers'} />} />
						<Route path={'inbox/carriers'} element={<RedirectWithSearch to={'/inbox/new_carriers'} />} />
						<Route path={'finance/shipper/billing/*'} element={<RedirectWithSearch to={'/finance/shipper_billing'} />} />
						<Route path={'finance/carrier/billing/*'} element={<RedirectWithSearch to={'/finance/carrier_billing'} />} />
						<Route path={'finance/claims'} element={<RedirectWithSearch to={'/finance/issues'} />} />
						<Route path={'finance/claimLines'} element={<RedirectWithSearch to={'/finance/issues'} />} />
						<Route path={'finance/claimLines/historical'} element={<RedirectWithSearch to={'/finance/historical-issues'} />} />
						<Route path={'finance/invoice-audit'} element={<RedirectWithSearch to={'/finance/invoice_management'} />} />
						<Route path={'configuration/index'} element={<RedirectWithSearch to={'/configuration/dashboard'} />} />
						<Route path={'configuration/region'} element={<RedirectWithSearch to={'/configuration/regions'} />} />
						<Route path={'configuration/trucktypes'} element={<RedirectWithSearch to={'/configuration/truck_types'} />} />
						<Route path={'config/truck'} element={<RedirectWithSearch to={'/configuration/truck_types'} />} />
						<Route path={'configuration/company'} element={<RedirectWithSearch to={'/configuration/company'} />} />
						<Route path={'company'} element={<RedirectWithSearch to={'/configuration/company'} />} />
						<Route path={'team'} element={<RedirectWithSearch to={'/configuration/company'} />} />
						<Route path={'shipper'} element={<RedirectWithSearch to={'/configuration/registered-shippers'} />} />
						<Route path={'shipper/overview'} element={<RedirectWithSearch to={'/configuration/registered-shippers'} />} />
						<Route path={'carrier/overview'} element={<RedirectWithSearch to={'/configuration/registered-carriers'} />} />
						<Route path={'*'} element={<Navigate to={'/transport/all-loads'} replace />} />
					</Route>
					<Route path={'transport'} element={<TransportRouter />}>
						<Route path={'redirect'} element={<RedirectWithSearch to={'/transport/all-loads'} />} />
						<Route index element={<AllLoads />} />
						<Route path={'all-loads'}>
							<Route index element={<AllLoads />} />
							<Route path={':shipmentID'} element={<ShipmentDetailsPage />} />
						</Route>
						<Route path={'shipment-docs'} element={<ShipmentDocs />} />
						<Route path={'pod-to-be-assigned'} element={<PodToBeAssigned />} />
						<Route path={'superdock'} element={<SuperDockRouter />}>
							<Route index element={<SuperDock />} />
							<Route path={'configure'} element={<Redirect path={'/transport/superdock'} />} />
							<Route path={'configure/:id'} element={<SuperDockConfigure />} />
							<Route path={'allocate'} element={<Redirect path={'/transport/superdock'} />} />
							<Route path={'allocate/:id'} element={<SuperDockAllocate />} />
							<Route path={':id'} element={<SuperDockDetails />} />
						</Route>
						<Route path={'carrier_recommendation'} element={<CarrierRecommendation />} />
					</Route>
					<Route path={'finance'} element={<FinanceRouter />}>
						<Route path={'carrier_billing'}>
							<Route index element={<FinanceCarrierBilling />} />
							<Route path={'detail'} element={<FinanceCarrierBillingDetail />} />
							<Route path={'detail/:id'} element={<FinanceCarrierBillingDetail />} />
						</Route>
						<Route path={'shipper_billing'}>
							<Route index element={<FinanceShipperBilling />} />
							<Route path={'detail'} element={<FinanceShipperBillingDetail />} />
							<Route path={'detail/:id'} element={<FinanceShipperBillingDetail />} />
						</Route>
						<Route path={'invoice_report'} element={<FinanceInvoiceReport />} />
						<Route path={'approvals'} element={<FinanceApproval />} />
						<Route path={'issues'} element={<FinanceIssueAndClaim />} />
						<Route path={'historical-issues'} element={<Navigate to={'/finance/issues?filter[status]=all'} replace />} />
						<Route path={'invoice_management/detail/:id'} element={<InvoiceManagementDetailPage />} />
						<Route path={'invoice_management/*'} element={<FinanceInvoiceManagement />} />
						<Route path={'fastpay_budget'} element={<FinanceFastPayBudget />} />
						{DEMURRAGE_CLAIMS_LIVE &&
							<Route path={'demurrage'} element={<FinanceDemurrageRouter />}>
								<Route path={'rate-cards'} element={<FinanceDemurrageRateCards />} />
							</Route>
						}
					</Route>
					<Route path={'configuration'} element={<ConfigurationRouter />}>
						<Route index path={'dashboard'} element={<ConfigurationDashboard />} />
						<Route path={'company'} element={<ConfigurationCompany />} />
						<Route path={'truck_types'} element={<ConfigurationTruckTypes />} />
						<Route path={'locations'} element={<ConfigurationLocations />} />
						<Route path={'auto-allocation'} element={<ConfigurationAutoAllocation />} />
						<Route path={'auto-allocation/create'} element={<ConfigurationAutoAllocationDetail />} />
						<Route path={'auto-allocation/duplicate'} element={<ConfigurationAutoAllocationDetail />} />
						<Route path={'auto-allocation/:id/detail'} element={<ConfigurationAutoAllocationDetail />} />

						<Route path={'fuel-levy'} element={<Navigate to='/configuration/fuel-levy/shipper' replace />} />
						<Route path={'fuel-levy/:type'} element={<ConfigurationFuelLevy />} />

						<Route path={'fuel-levies'} element={<Navigate to='/configuration/fuel-levies/shipper' replace />} />
						<Route path={'fuel-levies/:type'} element={<ConfigurationFuelLevies />} />
						<Route path={'fuel-levies/:type/:id'} element={<ConfigurationFuelLevyDetail />} />

						<Route path={'marketplace'} element={<ConfigurationMarketplace />} />
						<Route path={'locations/create'} element={<ConfigurationLocation/>} />
						<Route path='locations/:external_id' element={<ConfigurationLocation />} />
						<Route path={'locations/:external_id/:route'} element={<ConfigurationLocation/>} />
						<Route path={'locations/:external_id/shipper-details'} element={<ConfigurationLocationShipperDetails/>} />
						<Route path={'registered-carriers'} element={<ConfigurationRegisteredCarriers />} />
						<Route path={'registered-shippers'} element={<ConfigurationRegisteredShippers />} />
						<Route path={'ratecards'} element={<AdminRateCardRouter />}>
							<Route index element={<AdminRateCardHome />} />
							<Route path={'settings'} element={<AdminRateCardSettings />} />
							<Route path={'global'} element={<AdminRateCardGlobal />} />
							{/* only register following route when feature-flag is on */}
							{
								MARGIN_PERCENTAGE_LIVE && <Route path={'linked/create'}>
									<Route index element={<LinkedRateCardCreate />} />
									<Route path={'step1'} element={<LinkedRateCardCreate />} />
									<Route path={'step2'} element={<LinkedCarrierRateCard />} />
									<Route path={'step2/:id'} element={<LinkedCarrierRateCard />} />
									<Route path={'step3'} element={<LinkedShipperRateCard />} />
								</Route>
							}
							{
								MARGIN_PERCENTAGE_LIVE && <Route path={'linked/update'}>
									<Route path={'step2/:id'} element={<LinkedCarrierRateCard />} />
									<Route path={'step3/:id'} element={<LinkedShipperRateCard />} />
								</Route>
							}

							<Route path={'create'}>
								<Route index element={<AdminRateCardCreate />} />
								<Route path={'step1'} element={<AdminRateCardCreate />} />
								<Route path={'step2'} element={<AdminRateCard />} />
								<Route path={'step2/:id'} element={<AdminRateCard />} />
							</Route>
							<Route path={':id'} element={<AdminRateCard />} />
							<Route path={':id/history'} element={<HistoricalRateCard type={'Admin'} />} />
						</Route>
						<Route path={'regions'} element={<ConfigurationRegions />} />
						<Route path={'tags'} element={<ShipperTags />} />
						<Route path={'data-bulk-upload'} element={<ConfigurationDataBulkUpload />} />
						<Route path={'feature-flags'} element={<ConfigurationFeatureFlags />} />
					</Route>
				</Route>

				{/* NOT FOUND... */}
				<Route path={'*'} element={<NotFound />} />
			</Routes>
		</Suspense>
	);
}

export default Router;
